import styled, { type DefaultTheme } from 'styled-components';
import {
  DESKTOP_MIN_BREAK,
  MOBILE_MAX_BREAK,
  Z_INDEX_500,
  Z_INDEX_600,
} from '#/shared/constants';
import { ScreenReaderText } from '#/shared/components/Accessibility';
import { ClearButton } from '#/shared/components/Styled';
import LayersSVG from '#/shared/components/GeneratedIcons/trimet/Layers';
import CurrentLocationSVG from '#/shared/components/GeneratedIcons/trimet/CrosshairsGps';
import ZoomInSVG from '#/shared/components/GeneratedIcons/mapbuttons/ZoomIn';
import ZoomOutSVG from '#/shared/components/GeneratedIcons/mapbuttons/ZoomOut';
import CompassSVG from '#/shared/components/GeneratedIcons/mapbuttons/Compass';
import ThreeDSVG from '#/shared/components/GeneratedIcons/mapbuttons/ThreeD';
import MapAttributionLinks from '#/map/components/MapAttributionLinks';
import { focusCssRule } from '#/shared/css/focus';

interface IconAttrsProps {
  disabled?: boolean;
  theme: DefaultTheme;
}

export const MapButtonsWrapper = styled.div`
  /* Positioning */
  position: absolute;
  bottom: 0;
  right: 0;
  z-index: ${Z_INDEX_500};

  /* Layout */
  display: flex;
  justify-content: flex-end;

  /* Interaction */
  pointer-events: none;

  /* Enable pointer events for children */
  & > * {
    pointer-events: auto;
  }
`;

export const MapButtonsInner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: end;
  margin: 20px 10px;

  @media (min-width: ${DESKTOP_MIN_BREAK}) {
    margin: 0 20px;
  }
`;

export const LabelWrapper = styled(ScreenReaderText)`
  display: flex;
  margin: 0 5px 0 10px;
  font-size: 16px;
`;

export const IconWrapper = styled.div`
  /* Dimensions */
  width: 24px;
  height: 24px;

  /* Layout */
  display: flex;
  align-items: center;
  justify-content: center;
  justify-self: flex-end;
  margin: 0;
`;

const iconAttrs = (props: IconAttrsProps) => ({
  height: '100%',
  fill: props.theme.mapButtonIcon,
  fillOpacity: props.disabled ? props.theme.inactiveOpacity : 1,
});

export const CurrentLocationIcon = styled(CurrentLocationSVG).attrs(
  iconAttrs
)``;
export const LayersIcon = styled(LayersSVG).attrs(iconAttrs)``;
export const ZoomInIcon = styled(ZoomInSVG).attrs(iconAttrs)``;
export const ZoomOutIcon = styled(ZoomOutSVG).attrs(iconAttrs)``;
export const CompassIcon = styled(CompassSVG).attrs(iconAttrs)``;
export const ThreeDIcon = styled(ThreeDSVG).attrs(iconAttrs)``;

export const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.mapButtonBg};
  box-shadow: ${props => props.theme.cardOpenShadow};
  margin: 5px 0;
  border-radius: ${props => props.theme.buttonRadius};
`;

export const MapButton = styled(ClearButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.mapButtonText};
  background-color: transparent;
  height: 34px;
  width: 34px;
  position: relative;

  ${focusCssRule({ type: 'default' })}

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.cardBorder};
  }

  &:first-child {
    border-top-left-radius: ${props => props.theme.buttonRadius};
    border-top-right-radius: ${props => props.theme.buttonRadius};
  }

  &:last-child {
    border-bottom-left-radius: ${props => props.theme.buttonRadius};
    border-bottom-right-radius: ${props => props.theme.buttonRadius};
  }

  z-index: ${Z_INDEX_600};

  @media (any-hover: hover) {
    &:hover {
      color: ${props => props.theme.mapButtonTextHover};
      background-color: ${props => props.theme.mapButtonBg};
      cursor: pointer;
      svg {
        fill: ${props => props.theme.mapButtonIconHover};
      }
    }

    &:disabled:hover {
      cursor: default;
    }
  }

  &:disabled {
    cursor: not-allowed;
    pointer-events: none;
  }
`;

export const DesktopAttributionContainer = styled.div`
  @media (max-width: ${MOBILE_MAX_BREAK}) {
    // hide this element on mobile
    display: none;
  }

  display: flex;
  align-items: end;
  justify-content: end;
  background-color: ${({ theme }) => theme.mapAttributionDesktopBg};
  margin-top: 6px;
  padding: 1px 2px;
  border-radius: 5px 5px 0 0;
`;

export const AttributionLinks = styled(MapAttributionLinks)`
  font-size: 0.75rem;
  padding: 1px 4px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: underline;
  border-radius: ${props => props.theme.buttonRadius};

  && {
    // extra specificity (&&) is to override Leaflet inline styles, this
    // declaration can be moved out of this block once Leaflet goes, also
    // need to ensure this is less specific than the hover color selector
    color: ${({ theme }) => theme.mapAttributionDesktopText};
  }

  // the triple ampersand (&&&) and "important" used with this block supplies
  // the extra specificity needed to override inline styles coming from Leaflet,
  // those devices can be removed when Leaflet is removed from the project
  &&& {
    // "inset" style is used so the bottom of the focus indicator doesn't
    // extend past the bottom of the viewport
    ${focusCssRule({ type: 'inset', important: true })}
  }

  @media (any-hover: hover) {
    &:hover {
      background-color: transparent;
      color: ${({ theme }) => theme.mapAttributionDesktopTextHover};
      text-decoration: none;
    }
  }
`;
